import React, { useEffect, useState } from 'react';
import { BulkEquipmentUploadDialog } from './BulkEquipmentUploadDialog';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  FunctionField,
  SelectInput,
  BooleanInput,
  NumberInput,
  TextInput,
  useRecordContext,
  TopToolbar,
  CreateButton,
  ExportButton,
} from 'react-admin';
import { 
  Chip, 
  Box, 
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Avatar,
  Button
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  DirectionsCar as CarIcon,
  Construction as ConstructionIcon,
  Agriculture as TractorIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  PhotoCamera as PhotoIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { PageWrapper } from '../../components/PageWrapper';

// Equipment type choices based on new schema
const equipmentTypes = [
  { id: 'BACKHOE', name: 'Backhoe' },
  { id: 'BOX_TRUCK', name: 'Box Truck' },
  { id: 'BULLDOZER', name: 'Bulldozer' },
  { id: 'BUS', name: 'Bus' },
  { id: 'CAR', name: 'Car' },
  { id: 'COMPACTOR', name: 'Compactor' },
  { id: 'CONCRETE_MIXER', name: 'Concrete Mixer' },
  { id: 'CONSTRUCTION', name: 'Construction Equipment' },
  { id: 'CRANE', name: 'Crane' },
  { id: 'DELIVERY_VAN', name: 'Delivery Van' },
  { id: 'DUMP_TRUCK', name: 'Dump Truck' },
  { id: 'EXCAVATOR', name: 'Excavator' },
  { id: 'FLATBED', name: 'Flatbed' },
  { id: 'FORKLIFT', name: 'Forklift' },
  { id: 'GARBAGE_TRUCK', name: 'Garbage Truck' },
  { id: 'GRADER', name: 'Grader' },
  { id: 'LOADER', name: 'Loader' },
  { id: 'MOTOR_COACH', name: 'Motor Coach' },
  { id: 'OTHER', name: 'Other' },
  { id: 'PICKUP_TRUCK', name: 'Pickup Truck' },
  { id: 'PUMP_TRUCK', name: 'Pump Truck' },
  { id: 'REFRIGERATED_TRUCK', name: 'Refrigerated Truck' },
  { id: 'SCHOOL_BUS', name: 'School Bus' },
  { id: 'SEMI_TRUCK', name: 'Semi Truck' },
  { id: 'SKID_STEER', name: 'Skid Steer' },
  { id: 'TANKER', name: 'Tanker' },
  { id: 'TOW_TRUCK', name: 'Tow Truck' },
  { id: 'TRACTOR', name: 'Tractor' },
  { id: 'TRAILER', name: 'Trailer' },
  { id: 'TRUCK', name: 'Truck' },
  { id: 'VAN', name: 'Van' },
];

const ownershipTypes = [
  { id: 'OWNED', name: 'Owned' },
  { id: 'LEASED', name: 'Leased' },
];

const transmissionTypes = [
  { id: 'MANUAL', name: 'Manual' },
  { id: 'AUTOMATIC', name: 'Automatic' },
];

// Custom field components
const VehicleImageField = () => {
  const record = useRecordContext();
  const [preferThumbnailFallback, setPreferThumbnailFallback] = useState(false);

  const photo = record?.primaryPhoto;
  const thumbnailDataUrl =
    photo?.thumbnail && photo?.mimeType
      ? `data:${photo.mimeType};base64,${photo.thumbnail}`
      : null;

  useEffect(() => {
    setPreferThumbnailFallback(false);
  }, [record?.id, photo?.url, photo?.thumbnail]);

  const imageSrc = preferThumbnailFallback
    ? thumbnailDataUrl ?? null
    : photo?.url ?? thumbnailDataUrl ?? null;

  const getTypeInfo = (type: string) => {
    const vehicleTypes = ['CAR', 'VAN', 'TRUCK', 'BUS', 'PICKUP_TRUCK', 'DELIVERY_VAN', 'TRAILER', 'SEMI_TRUCK', 'BOX_TRUCK', 'FLATBED', 'TANKER', 'REFRIGERATED_TRUCK', 'GARBAGE_TRUCK', 'TOW_TRUCK', 'DUMP_TRUCK', 'CEMENT_TRUCK'];
    const heavyEquipment = ['EXCAVATOR', 'BULLDOZER', 'CRANE', 'LOADER', 'GRADER', 'COMPACTOR', 'BACKHOE', 'FORKLIFT', 'SKID_STEER', 'EQUIPMENT'];
    const agricultural = ['TRACTOR'];
    
    if (vehicleTypes.includes(type)) return { icon: <TruckIcon />, color: '#2196f3' };
    if (heavyEquipment.includes(type)) return { icon: <ConstructionIcon />, color: '#ff9800' };
    if (agricultural.includes(type)) return { icon: <TractorIcon />, color: '#4caf50' };
    return { icon: <CarIcon />, color: '#9e9e9e' };
  };
  
  const typeInfo = getTypeInfo(record?.type || '');
  
  return (
    <Box
      sx={{
        width: 80,
        height: 56,
        overflow: 'hidden',
        bgcolor: imageSrc ? 'transparent' : typeInfo.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        position: 'relative',
      }}
    >
      {imageSrc ? (
        <Box
          component="img"
          src={imageSrc}
          alt=""
          onError={() => {
            if (!preferThumbnailFallback && thumbnailDataUrl) {
              setPreferThumbnailFallback(true);
            }
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <Box sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {record?.type ? typeInfo.icon : <PhotoIcon />}
        </Box>
      )}
    </Box>
  );
};

const StatusField = () => {
  const record = useRecordContext();
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {record?.active ? (
        <>
          <ActiveIcon sx={{ color: '#4caf50', fontSize: 20 }} />
          <Typography variant="body2" color="#4caf50">Active</Typography>
        </>
      ) : (
        <>
          <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
          <Typography variant="body2" color="#ff9800">Inactive</Typography>
        </>
      )}
    </Box>
  );
};

const TypeField = () => {
  const record = useRecordContext();
  if (!record?.type) return null;
  
  const getTypeInfo = (type: string) => {
    const vehicleTypes = ['CAR', 'VAN', 'TRUCK', 'BUS', 'PICKUP_TRUCK', 'DELIVERY_VAN'];
    const heavyEquipment = ['EXCAVATOR', 'BULLDOZER', 'CRANE', 'LOADER', 'GRADER', 'COMPACTOR', 'BACKHOE'];
    const agricultural = ['TRACTOR'];
    
    if (vehicleTypes.includes(type)) return { icon: <TruckIcon />, color: '#2196f3' };
    if (heavyEquipment.includes(type)) return { icon: <ConstructionIcon />, color: '#ff9800' };
    if (agricultural.includes(type)) return { icon: <TractorIcon />, color: '#4caf50' };
    return { icon: <CarIcon />, color: '#9e9e9e' };
  };
  
  const typeInfo = getTypeInfo(record.type);
  const displayName = equipmentTypes.find(t => t.id === record.type)?.name || record.type;
  
  return (
    <Typography variant="body2">{displayName}</Typography>
  );
};

const VehicleDetailsField = () => {
  const record = useRecordContext();
  
  const details = [
    record?.manufacturedYear,
    record?.make,
    record?.model
  ].filter(Boolean).join(' ');
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <VehicleImageField />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {record?.name}
        </Typography>
        {details && (
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const IdentificationField = () => {
  const record = useRecordContext();
  
  return (
    <Stack spacing={0.5}>
      {record?.licensePlate && (
        <Typography variant="body2">
          <strong>Plate:</strong> {record.licensePlate}
          {record?.jurisdiction && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({record.jurisdiction})
            </Typography>
          )}
        </Typography>
      )}
      {record?.vin && (
        <Typography variant="caption" color="text.secondary">
          VIN: {record.vin}
        </Typography>
      )}
      {!record?.licensePlate && !record?.vin && (
        <Typography variant="caption" color="text.secondary">
          No identification
        </Typography>
      )}
    </Stack>
  );
};

const OwnershipField = () => {
  const record = useRecordContext();
  if (!record?.ownershipType) return <span>-</span>;
  
  return (
    <Chip 
      label={record.ownershipType} 
      color={record.ownershipType === 'OWNED' ? 'primary' : 'secondary'}
      size="small"
      variant="outlined"
    />
  );
};

// Filter component - wrapped in useMemo to prevent freezing
const useEquipmentFilters = () => {
  return React.useMemo(
    () => [
      <SelectInput 
        key="type" 
        source="type" 
        choices={equipmentTypes} 
        emptyText="All Types"
        emptyValue=""
      />,
      <BooleanInput 
        key="active" 
        source="active" 
        label="Active Only" 
        defaultValue={true} 
      />,
      <SelectInput 
        key="ownership" 
        source="ownershipType" 
        choices={ownershipTypes}
        emptyText="All Ownership Types"
        emptyValue="" 
      />,
      <NumberInput key="year" source="manufacturedYear" />,
      <TextInput key="make" source="make" />,
    ],
    []
  );
};

// Custom toolbar
const ListActions = () => {
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  
  return (
    <TopToolbar>
      <CreateButton label="Add Vehicle" />
      <ExportButton />
      <Button
        variant="contained"
        color="primary"
        startIcon={<UploadIcon />}
        onClick={() => setBulkUploadOpen(true)}
        sx={{ ml: 1 }}
      >
        Bulk Upload
      </Button>
      {bulkUploadOpen && (
        <BulkEquipmentUploadDialog
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
        />
      )}
    </TopToolbar>
  );
};

// Empty state component
const Empty = () => {
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={400}
      sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 4 }}
    >
      <TruckIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        No Equipment Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
        Track your fleet equipment, vehicles, and assets. Manage maintenance schedules and equipment assignments.
      </Typography>
      <Stack direction="row" spacing={2}>
        <CreateButton label="Add Equipment" variant="contained" />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setBulkUploadOpen(true)}
        >
          Bulk Upload
        </Button>
      </Stack>
      {bulkUploadOpen && (
        <BulkEquipmentUploadDialog
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
        />
      )}
    </Box>
  );
};

export const EquipmentList = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const filters = useEquipmentFilters();
  
  return (
    <PageWrapper title="Equipment">
      <List 
        filters={filters}
        actions={<ListActions />}
        empty={<Empty />}
        sort={{ field: 'name', order: 'ASC' }}
        perPage={25}
        disableSyncWithLocation={false}
      >
      <Datagrid 
        rowClick="show"
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
      >
        <VehicleDetailsField label="Vehicle" />
        <TypeField label="Type" />
        <StatusField label="Status" />
        <IdentificationField label="Identification" />
        <OwnershipField label="Ownership" />
      </Datagrid>
      </List>
    </PageWrapper>
  );
};